import Axios from '../../../config/axios-nextgen';

/**
 * Dashboard API Service
 * All dashboard-related API calls
 */

const dashboardService = {
    /**
     * Get user's dashboard with all widgets
     * @returns {Promise<UserDashboardDto>}
     */
    getUserDashboard() {
        return Axios.get('/api/dashboard')
            .then(response => response.data)
            .catch(error => {
                console.error('Error fetching dashboard:', error);
                throw error;
            });
    },

    /**
     * Get all available widget definitions
     * @param {string} category - Optional category filter ('user', 'system', etc.)
     * @returns {Promise<WidgetDefinitionDto[]>}
     */
    getWidgetDefinitions(category = null) {
        const params = category ? { category } : {};
        return Axios.get('/api/dashboard/widget-definitions', { params })
            .then(response => response.data)
            .catch(error => {
                console.error('Error fetching widget definitions:', error);
                throw error;
            });
    },

    /**
     * Add a new widget to the dashboard
     * @param {CreateDashboardWidgetRequest} request
     * @returns {Promise<UserDashboardDto>}
     */
    addWidget(request) {
        return Axios.post('/api/dashboard/widgets', request)
            .then(response => response.data)
            .catch(error => {
                console.error('Error adding widget:', error);
                throw error;
            });
    },

    /**
     * Update a specific widget
     * @param {string} userDashboardWidgetId
     * @param {UpdateDashboardWidgetRequest} request
     * @returns {Promise<UserDashboardDto>}
     */
    updateWidget(userDashboardWidgetId, request) {
        return Axios.put(`/api/dashboard/widgets/${userDashboardWidgetId}`, request)
            .then(response => response.data)
            .catch(error => {
                console.error('Error updating widget:', error);
                throw error;
            });
    },

    /**
     * Remove a widget from the dashboard
     * @param {string} userDashboardWidgetId
     * @returns {Promise<UserDashboardDto>}
     */
    removeWidget(userDashboardWidgetId) {
        return Axios.delete(`/api/dashboard/widgets/${userDashboardWidgetId}`)
            .then(response => response.data)
            .catch(error => {
                console.error('Error removing widget:', error);
                throw error;
            });
    },

    /**
     * Update complete dashboard layout (handles drag-and-drop)
     * @param {UpdateDashboardLayoutRequest} request
     * @returns {Promise<UserDashboardDto>}
     */
    updateDashboardLayout(request) {
        return Axios.put('/api/dashboard/layout', request)
            .then(response => response.data)
            .catch(error => {
                console.error('Error updating dashboard layout:', error);
                throw error;
            });
    },

    /**
     * Save widget filter configuration
     * @param {string} userDashboardWidgetId
     * @param {string} filterConfig - JSON string
     * @returns {Promise<void>}
     */
    saveWidgetFilters(userDashboardWidgetId, filterConfig) {
        return Axios.put(`/api/dashboard/widgets/${userDashboardWidgetId}/filters`, { filterConfig })
            .then(response => response.data)
            .catch(error => {
                console.error('Error saving widget filters:', error);
                throw error;
            });
    },

    /**
     * Toggle widget collapsed state
     * @param {string} userDashboardWidgetId
     * @param {boolean} isCollapsed
     * @returns {Promise<void>}
     */
    toggleCollapsed(userDashboardWidgetId, isCollapsed) {
        return Axios.patch(`/api/dashboard/widgets/${userDashboardWidgetId}/collapsed`, { isCollapsed })
            .then(response => response.data)
            .catch(error => {
                console.error('Error toggling collapsed state:', error);
                throw error;
            });
    }
};

export default dashboardService;

// Legacy test function for backwards compatibility
export function testGet() {
    return Axios.get('/api/patients/paged?page=1&pageSize=25')
        .then(response => response.data)
        .catch(error => console.log(error));
}